import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "@tanstack/react-router";
import { useCatalog } from "../data/useCatalog";
import { sideTag } from "../lib/gameMeta";
import type { CatalogFilters, GameDetail, Shelf } from "../lib/types";

function railShelf(catalog: string): Shelf {
  return catalog === "games" ? "playable" : "lab";
}

// The player-page catalog rail: a search box + an alphabetical list that loads
// more as you scroll. (Search-and-scroll, rather than the old 2,524-link dump.)
export function SideRail({ game }: { game: GameDetail }) {
  const [text, setText] = useState("");
  const [q, setQ] = useState("");
  useEffect(() => {
    const id = setTimeout(() => setQ(text), 200);
    return () => clearTimeout(id);
  }, [text]);

  const filters: CatalogFilters = useMemo(
    () => ({ shelf: railShelf(game.catalog), search: q, category: "", source: "", sort: "az" }),
    [game.catalog, q],
  );
  const { items, fetchNextPage, hasNextPage, isFetchingNextPage } = useCatalog(filters);
  const railRef = useRef<HTMLElement>(null);

  const onScroll = () => {
    const el = railRef.current;
    if (!el) return;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 200 && hasNextPage && !isFetchingNextPage) {
      void fetchNextPage();
    }
  };

  // Bring the current game into view once it's loaded into the rail.
  const scrolledFor = useRef<string | null>(null);
  useEffect(() => {
    if (scrolledFor.current === game.id) return;
    const rail = railRef.current;
    const active = rail?.querySelector<HTMLElement>("a.active");
    if (rail && active) {
      rail.scrollTop = Math.max(0, active.offsetTop - rail.clientHeight / 2);
      scrolledFor.current = game.id;
    }
  }, [game.id, items.length]);

  return (
    <aside className="shelf-rail" aria-label="Catalog" ref={railRef} onScroll={onScroll}>
      <input
        type="search"
        placeholder="Find a title"
        aria-label="Find a title"
        autoComplete="off"
        value={text}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Escape") setText("");
        }}
      />
      <nav id="sideList" aria-label="All games">
        {items.map((item) => {
          const active = item.id === game.id && item.catalog === game.catalog;
          return (
            <Link
              key={`${item.catalog}:${item.id}`}
              to="/game/$id"
              params={{ id: item.id }}
              search={item.catalog !== "games" ? { catalog: item.catalog } : {}}
              className={active ? "active" : undefined}
              aria-current={active ? "page" : undefined}
            >
              {item.thumbnail ? (
                <img src={item.thumbnail} alt="" loading="lazy" decoding="async" />
              ) : (
                <span className="rail-blank" aria-hidden="true" />
              )}
              <span>{item.title}</span>
              <small>{sideTag(item)}</small>
            </Link>
          );
        })}
      </nav>
    </aside>
  );
}
